import React, { useEffect, useState } from "react";
import { Check, Copy, ExternalLink, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { t } from "@/i18n";
import { API, createEternalLink, deleteEternalLink, fetchEternalLinks, updateEternalLink } from "@/api";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  slug: "",
  target_url: "",
  utm_source: "",
  utm_medium: "",
  utm_campaign: "",
};

const EternalLinksManager = ({ lang }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const refresh = async () => {
    try {
      setItems(await fetchEternalLinks());
    } catch (e) {
      console.error(e);
      toast.error("Failed to load eternal links");
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const eternalUrl = (slug) => `${API}/r/${slug}`;

  const startEdit = (item) => {
    setEditingId(item.id);
    setForm({
      name: item.name || "",
      slug: item.slug || "",
      target_url: item.target_url || "",
      utm_source: item.utm_source || "",
      utm_medium: item.utm_medium || "",
      utm_campaign: item.utm_campaign || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.target_url) {
      toast.error(t(lang, "fillEternalFields"));
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, slug: form.slug || undefined };
      if (editingId) {
        await updateEternalLink(editingId, payload);
      } else {
        await createEternalLink(payload);
      }
      toast.success(t(lang, "saved"));
      cancelEdit();
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (item) => {
    await navigator.clipboard.writeText(eternalUrl(item.slug));
    setCopiedId(item.id);
    toast.success(t(lang, "copied"));
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleToggle = async (item) => {
    try {
      await updateEternalLink(item.id, { is_active: !item.is_active });
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t(lang, "confirmDelete"))) return;
    try {
      await deleteEternalLink(id);
      toast.success(t(lang, "deleted"));
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-5" data-testid="eternal-links-manager">
      <form onSubmit={handleSubmit} className="space-y-3 p-4 border border-white/10 rounded-md bg-black/40" data-testid="eternal-link-form">
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "eternalName")}</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="eternal-name"
              placeholder="PDlink"
              className="bg-black border-white/15 mt-1"
            />
          </div>
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "eternalSlug")}</Label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              data-testid="eternal-slug"
              placeholder="pdlink"
              className="bg-black border-white/15 mt-1"
            />
          </div>
        </div>
        <div>
          <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">{t(lang, "targetUrl")}</Label>
          <Input
            value={form.target_url}
            onChange={(e) => setForm({ ...form, target_url: e.target.value })}
            data-testid="eternal-target-url"
            placeholder="https://t.me/VarenikTop_paybot?start=catalog_..."
            className="bg-black border-white/15 mt-1"
          />
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">utm_source</Label>
            <Input value={form.utm_source} onChange={(e) => setForm({ ...form, utm_source: e.target.value })} data-testid="eternal-utm-source" className="bg-black border-white/15 mt-1" />
          </div>
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">utm_medium</Label>
            <Input value={form.utm_medium} onChange={(e) => setForm({ ...form, utm_medium: e.target.value })} data-testid="eternal-utm-medium" className="bg-black border-white/15 mt-1" />
          </div>
          <div>
            <Label className="font-mono-display text-[10px] uppercase tracking-widest text-zinc-400">utm_campaign</Label>
            <Input value={form.utm_campaign} onChange={(e) => setForm({ ...form, utm_campaign: e.target.value })} data-testid="eternal-utm-campaign" className="bg-black border-white/15 mt-1" />
          </div>
        </div>
        <div className="flex gap-2">
          <Button type="submit" disabled={submitting} data-testid="eternal-submit" className="bg-[#FF1493] hover:bg-[#ff3aa3] text-black font-bold uppercase tracking-widest">
            {editingId ? <Pencil className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            {editingId ? t(lang, "save") : t(lang, "addEternal")}
          </Button>
          {editingId && (
            <Button type="button" variant="outline" onClick={cancelEdit} data-testid="eternal-cancel" className="border-white/15 bg-transparent text-white hover:bg-white/5 hover:text-white">
              {t(lang, "cancel")}
            </Button>
          )}
        </div>
      </form>

      <div className="space-y-2" data-testid="eternal-link-list">
        {items.map((item, idx) => (
          <div key={item.id} data-testid={`eternal-link-row-${idx}`} className="p-3 border border-white/10 rounded-md bg-black/30 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono-display text-sm text-white uppercase tracking-wide flex items-center gap-2">
                  {item.name}
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${item.is_active ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`} data-testid={`eternal-status-${idx}`}>
                    {item.is_active ? t(lang, "active") : t(lang, "inactive")}
                  </span>
                </div>
                <div className="font-mono-display text-[10px] text-[#FFE600] truncate">{eternalUrl(item.slug)}</div>
                <div className="font-mono-display text-[10px] text-zinc-500 truncate">{item.target_url}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-display text-2xl text-[#FF1493]" data-testid={`eternal-clicks-${idx}`}>{item.clicks}</div>
                <div className="font-mono-display text-[9px] uppercase tracking-widest text-zinc-500">{t(lang, "clicks")}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 justify-end">
              <button type="button" onClick={() => handleCopy(item)} data-testid={`eternal-copy-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#FFE600]" aria-label="copy">
                {copiedId === item.id ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
              <a href={eternalUrl(item.slug)} target="_blank" rel="noopener noreferrer" data-testid={`eternal-open-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" aria-label="open">
                <ExternalLink className="w-4 h-4" />
              </a>
              <button type="button" onClick={() => handleToggle(item)} data-testid={`eternal-toggle-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-emerald-400" aria-label="toggle">
                <Power className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => startEdit(item)} data-testid={`eternal-edit-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#FF1493]" aria-label="edit">
                <Pencil className="w-4 h-4" />
              </button>
              <button type="button" onClick={() => handleDelete(item.id)} data-testid={`eternal-delete-${idx}`} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-red-500" aria-label="delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default EternalLinksManager;
